/* eslint-disable no-extra-boolean-cast */
import PaymentRepository from "@/utilities/repositories/Payment";
import {
  EXeroPaymentStatus,
  EXeroPaymentType,
  IXeroPayment,
} from "@/utilities/types/Payment";
import * as React from "react";
import { toast } from "react-hot-toast";
import { FiRefreshCw } from "react-icons/fi";
import { Button, Loader, Title } from "rizzui";
import PaymentStatus from "./payment-verify/payment-status";
import PaymentInformation from "./payment-verify/payment-information";
import { IVoucher } from "@/utilities/types/Voucher";
import { useModal } from "@/app/shared/modal-views/use-modal";
import UploadScreenShotNotification from "./upload-screenshot-notification";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import SelectPaymentMethod from "../payment-method";
import { useAtom } from "jotai";
import { isPayNowCompletedAtom } from "@/store/atoms/pay_now_atom";
import { EXeroPaymentMethod } from "@/utilities/enums/Payment";
import CreditCardInstructions from "../payment/credit-card-instructions";
import { PAYMENT_CUSTOM_SIZE } from "@/config/constants";
import { IPromotionDTO } from "@/utilities/types/Promotion";
import PaymentAdCreditInformation from "./payment-verify/payment-ad-credit-information";

export interface PaymentVerifyProps {
  typeId?: string;
  onPaymentComplete?: (payment?: IXeroPayment | null) => Promise<void>;
  voucher?: IPromotionDTO | null;
  canSkip?: boolean;
  isConfirm?: boolean;
  paymentConfirm?: IXeroPayment | null;
  nextPage?: () => void;
}

export default function PaymentVerify({
  typeId,
  onPaymentComplete,
  voucher,
  canSkip,
  isConfirm,
  paymentConfirm,
  nextPage,
}: PaymentVerifyProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [payment, setPayment] = React.useState<IXeroPayment | null>(null);
  const [paymentLink, setPaymentLink] = React.useState<string | null>(null);
  const [isPayNowCompleted, setIsPayNowCompleted] = useAtom(
    isPayNowCompletedAtom
  );

  const fetchPaymentDetails = React.useCallback(async () => {
    if (isConfirm) return;
    try {
      setIsLoading(true);
      const response = await PaymentRepository.findOrCreateXeroPayment({
        typeId: typeId || "",
        type: paymentConfirm?.type || EXeroPaymentType.PACKAGE,
        promotionCode: voucher?.code,
      });
      setPayment(response.data);
      setPaymentLink(response?.data?.onlineInvoiceUrl || null);
    } catch (error) {
      toast.error("Failed to fetch payment details");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [typeId, isConfirm, voucher?.code]);

  const handleClickPaymentComplete = () => {
    navigate("/", { replace: true });
    window.location.reload();
  };

  const checkPaymentStatus = React.useCallback(async () => {
    if (!payment?.id) return;
    try {
      setIsRefreshing(true);
      const response = await PaymentRepository.findXeroPayment(payment.id);

      const paymentData = response?.data;

      const paymentStatus = paymentData?.status;

      if (paymentStatus === EXeroPaymentStatus.SUCCEED) {
        setPayment({
          ...paymentData,
          status: EXeroPaymentStatus.SUCCEED,
        });
        toast.success("Payment completed successfully!");
        await onPaymentComplete?.(paymentData);
      } else if (paymentStatus === EXeroPaymentStatus.FAILED) {
        toast.error("Payment failed");
        await onPaymentComplete?.(paymentData);
      } else {
        setPayment(paymentData);

        toast("Payment is still pending", {
          icon: "⏳",
        });
      }
    } catch (error) {
      toast.error("Failed to check payment status");
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  }, [payment?.id, onPaymentComplete]);

  React.useEffect(() => {
    //confirm payment
    if (!!paymentConfirm && isConfirm) {
      setPayment(paymentConfirm);
      return;
    }
    fetchPaymentDetails();
  }, [fetchPaymentDetails, isConfirm, paymentConfirm]);

  React.useEffect(() => {
    if (!!paymentConfirm) {
      checkPaymentStatus();
    }
  }, [paymentConfirm, checkPaymentStatus]);

  const { openModal, closeModal } = useModal();

  const methods = useForm({
    reValidateMode: "onChange",
    mode: "all",
  });

  const onOpenModalPaymentSelect = (initMethod?: EXeroPaymentMethod) => {
    openModal({
      view: (
        <SelectPaymentMethod
          initMethod={initMethod}
          onPayNowClick={() => {
            closeModal();
            onOpenModalPayNow();
          }}
          openModalCreditCard={openModalCreditCard}
          onClose={closeModal}
        />
      ),
      customSize: PAYMENT_CUSTOM_SIZE,
    });
  };

  const onOpenModalPayNow = () => {
    openModal({
      view: (
        <FormProvider {...methods}>
          <UploadScreenShotNotification
            payment={payment}
            onClose={closeModal}
            onBack={(initMethod?: EXeroPaymentMethod) =>
              onOpenModalPaymentSelect(initMethod)
            }
            onPayNowComplete={onPayNowComplete}
          />
        </FormProvider>
      ),
      customSize: PAYMENT_CUSTOM_SIZE,
    });
  };

  const openModalCreditCard = () => {
    openModal({
      view: (
        <CreditCardInstructions
          paymentLink={paymentLink || ""}
          onClose={closeModal}
          onBack={(initMethod?: EXeroPaymentMethod) =>
            onOpenModalPaymentSelect(initMethod)
          }
          handleClickPaymentComplete={handleClickPaymentComplete}
        />
      ),
      customSize: PAYMENT_CUSTOM_SIZE,
    });
  };
  const onPayNowComplete = () => {
    setIsPayNowCompleted(true);
  };

  const goToDashboard = () => {
    navigate("/", { replace: true });
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader variant="spinner" size="xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="rounded-lg bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.1)]">
        <div className="mb-6 flex items-center justify-between">
          <Title className="text-2xl font-bold">Payment Verification</Title>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={checkPaymentStatus}
            isLoading={isRefreshing}
          >
            <FiRefreshCw className="h-4 w-4" />
            Refresh Status
          </Button>
        </div>

        {paymentConfirm &&
        paymentConfirm?.type === EXeroPaymentType.AD_CREDIT ? (
          <PaymentAdCreditInformation payment={paymentConfirm} />
        ) : (
          payment && <PaymentInformation payment={payment} />
        )}

        <div className="mt-8">
          <PaymentStatus
            status={payment?.status || EXeroPaymentStatus.PENDING}
          />
        </div>

        <div className="mt-8 flex flex-col gap-2">
          {payment?.status === EXeroPaymentStatus.SUCCEED ? (
            <Button className="flex-1" onClick={nextPage}>
              Next Page
            </Button>
          ) : isPayNowCompleted ? (
            <Button className="flex-1" onClick={goToDashboard}>
              View Dashboard
            </Button>
          ) : paymentLink ? (
            <Button
              disabled={!paymentLink}
              className="flex-1"
              onClick={() => onOpenModalPaymentSelect()}
            >
              Make Payment
            </Button>
          ) : null}

          {/* {canSkip && paymentLink && (
            <p className="text-center text-gray-500 font-medium">or</p>
          )} */}

          {/* {canSkip && (
            <Button
              variant="outline"
              className="flex-1 inline-flex items-center gap-2"
              onClick={() => onPaymentComplete?.(payment)}
            >
              Next Step
              <IoArrowForward className="text-lg" />
            </Button>
          )} */}
        </div>
      </div>
    </div>
  );
}
